import React from 'react';
import cn from 'classnames';
import classes from './ControlGroups.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { Button, Dropdown, MenuProps, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import dayjs from 'dayjs';
import { setAddDropdownOpen, setIsEditRoomModalOpen, setIsInviteUserOpen, setIsRSidebarCollapsed, setIsTariffInfoModalOpen, setIsTourOpen, setIsVisibleUploadModal, setTariffInfoModalRoom, setUploadDropdownOpen } from 'store/slices/windowStateSlice';
import { ClockCircleOutlined } from '@ant-design/icons';
import { getTheme } from 'helpers/getTheme';
import RowLoader from 'components/Sceletons/RowLoader';
import Assets from 'assets/icons';
import { DocumentsType } from 'interfaces';
import { setDocumentsType } from 'store/slices/dataDocumentsSlice';
import { setIsEmptyFileCreateOpen, setIsFileCreateOpen, setIsFolderCreateOpen } from 'store/slices/dataRoomSlice';
import fetchGroupsAndUsers, { fetchPermissions } from 'store/reducers/usersGroupCreator';
import { setSelectedDataRoom } from 'store/slices/userDataSlice';
import { getTariffProfileName } from 'components/TariffsComponent/tariffsData';

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

type ControlTableType = {
  onSegmentCahnge: (value: string | number) => Promise<void>;
  isAvailableUploading: boolean;
}

const ControlTable = ({isAvailableUploading, onSegmentCahnge}: ControlTableType) => {
  
  const { dataRoom, isLoadingRoom } = useAppSelector((state) => state.dataRoom);
  const { userData, isLoadingUserData, isLoadingRooms, userPermissions } = useAppSelector((state) => state.userData);
  const { selectedKeys, rSidebarEntity, isTourOpen, isVisibleUploadModal,
    messageApi, uploadDropdownOpen, addDropdownOpen } = useAppSelector((state) => state.windowState);
  const { groups, rolesPermissions } = useAppSelector((state) => state.dataUsers);
  const { currentFolder } = useAppSelector((state) => state.documents);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  
  const isAvailableSettings = userPermissions?.can_edit_room_settings;
  const banManageRoomSetting = dataRoom?.access_terms_status === 'waiting_for_approve' || dataRoom?.is_blocked;
  const isLoadinInformation = isLoadingRoom || isLoadingUserData || isLoadingRooms;
  const dateEndedUtc = dayjs.utc(dataRoom?.tariff?.tariff_ends_at).valueOf();
  const currentDate = dayjs().valueOf();
  const tariffTimeLeft = dateEndedUtc - currentDate;
  const isPeriodEnded = tariffTimeLeft < 0;
  const isTrialTariff = dataRoom?.tariff?.tariff_name === 'trial';
  const daysLeftTotal = Math.ceil(Math.max(0, tariffTimeLeft / MILLISECONDS_IN_DAY));
  const tariffDaysLeftLabel = `${daysLeftTotal} ${t('navigation.days')}`;
  const isExpiringSoon = !isPeriodEnded && (tariffTimeLeft / MILLISECONDS_IN_DAY) < 3;
  const isExperimentalAvailable = userData?.is_experimental_available;
  const isAvailableCreateFolder = userPermissions?.can_create_folder;
  const isAvailableInviteUsers = userPermissions?.can_invite_users;

  React.useEffect(() => {
    const addFolder = document.querySelector('.item-menu-new-folder');
    const addUsers = document.querySelector('.item-menu-add-participants');
    if (addFolder) addFolder.setAttribute('data-testid', 'room-menu-new-folder');
    if (addUsers) addUsers.setAttribute('data-testid', 'room-menu-add-participants');
  }, [addDropdownOpen]);

  React.useEffect(() => {
    if (isVisibleUploadModal) {
      dispatch(setAddDropdownOpen(false));
      dispatch(setUploadDropdownOpen(false));
    }
  }, [isVisibleUploadModal]);
  
  const closeTourElem = () => isTourOpen && dispatch(setIsTourOpen(false));
  
  const onUploadClick = (type: DocumentsType) => {
    // closeTourElem();
    selectedKeys[0] !== 'documents' && onSegmentCahnge('documents');
    dispatch(setDocumentsType(type as DocumentsType));
    dispatch(setIsVisibleUploadModal(true));
  };
  
  const onCreateFolderClick = () => {
    if (currentFolder.length > 14) {
      messageApi?.warning(t('Documents.management.max15level', {level: 14+1}), 12)
      return;
    }

    selectedKeys[0] !== 'documents' && onSegmentCahnge('documents');
    dispatch(setIsFolderCreateOpen(true));
  };

  const onCreateFile = () => {
    selectedKeys[0] !== 'documents' && onSegmentCahnge('documents');
    dispatch(setIsFileCreateOpen(true));
  };

  const onCreateEmptyFile = () => {
    selectedKeys[0] !== 'documents' && onSegmentCahnge('documents');
    dispatch(setIsEmptyFileCreateOpen(true));
  };

  const onInviteUsersClick = () => {
    !rolesPermissions[0] && dispatch(fetchPermissions({ t }));
    !groups[0] &&
      dataRoom &&
      dispatch(fetchGroupsAndUsers({ room_id: dataRoom?.id }));
    dispatch(setIsInviteUserOpen(true));
  };

  const openTariffInfoModal = () => {
    if (!dataRoom) return;
    dispatch(setTariffInfoModalRoom(dataRoom));
    dispatch(setIsTariffInfoModalOpen(true));
  };

  const tariffNameKey = getTariffProfileName(dataRoom?.tariff);

  const tariffPopoverContent = (
    <div className={classes.tariffPopover}>
      <div className={classes.tariffPopoverRow}>
        <span>{t('TariffInfoModal.tariffName')}</span>
        <b>
          {tariffNameKey
            ? t(`Tariffs.tariff_name.${tariffNameKey}`)
            : '—'}
        </b>
      </div>
      {dataRoom?.tariff?.tariff_ends_at && (
        <div className={classes.tariffPopoverRow}>
          <span>{t('TariffInfoModal.tariffEndsAt')}</span>
          <b>
            {getDateWithTimeZone(
              String(dayjs(dataRoom?.tariff?.tariff_ends_at)),
              userData?.date_format || 'international'
            )}
          </b>
        </div>
      )}
      <div className={classes.tariffPopoverRow}>
        <span>{t('navigation.left')}</span>
        {isPeriodEnded ? (
          <b className={classes.tariffPopoverExpired}>
            {isTrialTariff
              ? t('Documents.management.trialEnded')
              : t('Rooms.expiredTariff')}
          </b>
        ) : (
          <b>{tariffDaysLeftLabel}</b>
        )}
      </div>
      <div className={classes.tariffPopoverFooter}>
        <Button type='primary' size='small' onClick={openTariffInfoModal}>
          {t('TariffInfoModal.title')}
        </Button>
      </div>
    </div>
  );

  // Элементы для кнопки "Добавить файлы"
  const newFilesItems: MenuProps['items'] = [];

  newFilesItems.push({
    label: (
      <span className={classes.spanItem} id='upload-popup-container-1'>
        <Assets.FileUploadIcon /> {t('Documents.management.uploadFiles')}
      </span>
    ),
    onClick: () => onUploadClick('files'),
    className: classes.menuItem,
    key: '0',
  });

  if (dataRoom?.agreement_file && dataRoom?.user_status === 2) {
    newFilesItems.push({
      type: 'divider',
      className: classes.divider,
    });
    newFilesItems.push({
      label: (
        <span className={classes.spanItem} id='upload-popup-container-2'>
          <Assets.FileUploadIcon />
          <Assets.ShieldIcon className={classes.shield} />
          {t('Documents.management.uploadConfidentialFiles')}
        </span>
      ),
      onClick: () => onUploadClick('confidentialFiles'),
      className: classes.menuItem,
      key: '1',
    });
  };
  
  newFilesItems.push({
    type: 'divider',
    className: classes.divider,
  });
    
  newFilesItems.push({
    label: (
      <span className={classes.spanItem} id='upload-popup-container-3'>
        <Assets.FileAddIcon /> {t('Documents.management.createFile')}
      </span>
    ),
    disabled: !isAvailableUploading,
    onClick: onCreateEmptyFile,
    className: classes.menuItem,
    key: 'createFile',
  });
  
  if (isExperimentalAvailable) {
    newFilesItems.push({
      type: 'divider',
      className: classes.divider,
    });
    newFilesItems.push({
      label: (  
        <span className={classes.spanItem} id='upload-popup-container-4'>
          <Assets.FileAddIcon /> {t('Documents.management.generateFile')}
        </span>
      ),
      disabled: !isAvailableUploading,
      onClick: onCreateFile,
      className: classes.menuItem,
      key: 'generateFile',
    });
  }

  // Элементы для кнопки "+"
  const addItems: MenuProps['items'] = [];
  
  addItems.push({
    label: (
      <span className={classes.spanItem} id='add-popup-container-1'>
        <Assets.FolderAddIcon /> {t('Documents.management.newFolder')}
      </span>
    ),
    disabled: !isAvailableCreateFolder,
    onClick: onCreateFolderClick,
    className: cn(classes.menuItem, 'item-menu-new-folder'),
    key: 2,
  });

  if (isAvailableInviteUsers) {
    addItems.push({
      type: 'divider',
      className: classes.divider,
    });
    addItems.push({
      label: (
        <span className={classes.spanItem} id='add-popup-container-2'>
          <Assets.MemberAddIcon /> {t('Documents.management.addUsers')}
        </span>
      ),
      onClick: onInviteUsersClick,
      className: cn(classes.menuItem, 'item-menu-add-participants'),
      key: 3,
    });
  }
  
  const onOpenChangeTable = (
    open: boolean,
    info: { source: 'trigger' | 'menu' }
  ) => {
    // closeTourElem();
    dispatch(setAddDropdownOpen(open));
  };

  const onOpenChangeUpload = (open: boolean) => {
    // closeTourElem();
    !isTourOpen && dispatch(setUploadDropdownOpen(open));
  };

  const openEditRoomModal = () => {
    dispatch(setSelectedDataRoom(dataRoom!));
    dispatch(setIsEditRoomModalOpen(true));
  };
  

  const openRightSideBar = (section: string, width: number) => {
    !rSidebarEntity || section !== rSidebarEntity
      ? dispatch(
          setIsRSidebarCollapsed({
            collapsed: false,
            entity: section,
            width,
          })
        )
      : dispatch(
          setIsRSidebarCollapsed({
            collapsed: true,
            entity: '',
            width: 0,
          })
        );
  };

  return (
    <div className={classes.iconsGroup}>
      <div>
        {Boolean(dataRoom?.tariff?.tariff_ends_at) && (
          <Popover
            content={tariffPopoverContent}
            placement='bottomRight'
            trigger='hover'
            classNames={{ root: getTheme() }}
          >
            <button
              type='button'
              className={cn(classes.tariffChip, {
                [classes.tariffChipWarning]: isExpiringSoon && !isPeriodEnded,
                [classes.tariffChipDanger]: isPeriodEnded,
              })}
            >
              <span className={classes.tariffChipIconWrap}>
                <ClockCircleOutlined />
              </span>
              {isPeriodEnded ? (
                <span>
                  {isTrialTariff
                    ? t('Documents.management.trialEnded')
                    : t('Rooms.expiredTariff')}
                </span>
              ) : (
                <span>{tariffDaysLeftLabel}</span>
              )}
            </button>
          </Popover>
        )}
      </div>
      {isLoadinInformation ? (
        <RowLoader width='200px' padding='2px 0 0 0' height='27px' />
      ) : (<>
        <Dropdown
          menu={{ items: newFilesItems }}
          onOpenChange={onOpenChangeUpload}
          open={uploadDropdownOpen}
          disabled={!isAvailableUploading || banManageRoomSetting}
          trigger={['click']}
          classNames={{ root: classes.addMenu }}
        >
          <Button
            id='room-toolbar-add-files'
            size='middle'
            type='primary'
            color='primary'
            disabled={!isAvailableUploading || banManageRoomSetting}
          >
            {t('Documents.management.addFiles')}
          </Button>
        </Dropdown>
        <Dropdown
          menu={{ items: addItems }}
          onOpenChange={onOpenChangeTable}
          open={addDropdownOpen}
          disabled={
            dataRoom?.access_terms_status === 'waiting_for_approve' ||
            dataRoom?.is_blocked
          }
          trigger={['click']}
          classNames={{ root: classes.addMenu }}
        >
          <Button
            className={classes.plusButton}
            icon={<Assets.PlusIcon />}
            data-testid='room-toolbar-add-menu'
            id='room-toolbar-add-menu'
            shape='circle'
            size='small'
            type='primary'
          />
        </Dropdown>

        {isAvailableSettings && (
          <Button
            className={cn(
              classes.settingButton,
              banManageRoomSetting ? classes.disable : 'null'
            )}
            icon={<Assets.EditIcon />}
            disabled={banManageRoomSetting}
            type='text'
            onClick={openEditRoomModal}
          />
        )}

        <Button
          id='room-toolbar-settings'
          data-testid='room-toolbar-settings'
          className={cn(
            classes.settingButton,
            banManageRoomSetting ? classes.disable : 'null'
          )}
          icon={<Assets.SettingsIcon />}
          disabled={banManageRoomSetting}
          type='text'
          onClick={() => {
            // closeTourElem();
            openRightSideBar('room_setting', 300);
          }}
        />

      </>)}
    </div>
  );
}

export default ControlTable;
